import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { faClipboardList, faGear } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import { useFocusEffect, useIsFocused, useNavigationState } from '@react-navigation/native';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import { Tabs } from '../../../components/Tabs';
import DetectionDetails from './DetectionDetails';
import Detections from './Detections';
import DetectionRoutes from './DetectionRoutes';
import GoBackModal from './GoBackModal';
import { useAuth } from '../../../context/Auth';
import CustomButton from '../../../components/CustomButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';

interface DetectionProps {
    navigation: Navigation;
    route: {
        params: {
            detectionId: string;
        };
    };
    page: 'live-events' | 'live-alerts' | 'search' | 'map';
}

export default function Detection({ navigation, route, page }: DetectionProps) {
    const [detection, setDetection] = useState<LprDetection>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [detectionRoutes, setDetectionRoutes] = useState<{ name: string; cameras: CameraMap[]; }[]>([]);
    const { user: authenticatedUser } = useAuth();

    const index = useNavigationState(state => state);

    async function getDetection(detectionId: string) {
        try {
            const detection = await licensePlateRecognitionService.getLprDetection(Number(detectionId));
            setDetection(detection);
        } catch (err) {
            console.error(err);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getDetection(route.params.detectionId);
        }
    }, [isFocused, navigation, route]));

    function goBack() {
        if (index.routes.length > 1) {
            return navigation.goBack();
        } else {
            if (page == 'live-alerts') {
                window.location.href = '/LPR/live-alerts';
            }
            if (page == 'live-events') {
                window.location.href = '/LPR/live-events';
            }
            if (page == 'search') {
                window.location.href = '/LPR/search';
            }
            if (page == 'map' && detection) {
                window.location.href = detection.camera ? `/cameras/map/${detection.camera.id}` : '/cameras/map';
            }
        }
    }

    if (!detection || !authenticatedUser) {
        return <></>;
    }

    return <Container>
        <Header
            title={translate('detection')}
            goBackAction={() => {
                if (detectionRoutes.length) {
                    setIsModalVisible(true);
                } else {
                    goBack();
                }
            }}
            actions={<View style={{ flexDirection: 'row', gap: 10 }}>
                {detection.occurrence && (authenticatedUser?.isAdmin
                    || authenticatedUser?.permissions.dispatch_module_access
                    || authenticatedUser?.permissions.dispatch_manage_occurrences
                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences
                    || authenticatedUser?.permissions.reopen_occurrence
                    || authenticatedUser?.permissions.transfer_occurrence
                ) ?
                    <CustomButton
                        text={translate('openOccurrence')}
                        icon={faClipboardList}
                        type='secondary'
                        onPress={() => window.open(`/dispatch/occurrences/detail/${detection.occurrence?.id}`)} /> : null
                }
                {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.crm_module_access) && detection.camera ?
                    <CustomButton
                        text={translate('cameraConfiguration')}
                        icon={faGear}
                        type='secondary'
                        onPress={() => window.open(`/CRM/cameras/details/${detection.camera?.id}`)} /> : null
                }
            </View>}
        />
        <Card>
            <Tabs tabs={[{
                key: 'vehicleData',
                label: translate('vehicleData')
            }, {
                key: 'detections',
                label: translate('detections')
            }, {
                key: 'routes',
                label: translate('routes')
            }]}>
                {({ selectedTab }) => {
                    if (selectedTab == 'vehicleData') {
                        return (<DetectionDetails navigation={navigation} detection={detection} onSave={() => getDetection(route.params.detectionId)} />);
                    } else if (selectedTab == 'detections') {
                        return (<Detections setDetectionRoutes={setDetectionRoutes} detectionRoutes={detectionRoutes} detection={detection} />);
                    } else if (selectedTab == 'routes') {
                        return (<DetectionRoutes detectionRoutes={detectionRoutes} />);
                    }
                }}
            </Tabs>
        </Card>
        <GoBackModal
            isModalVisible={isModalVisible}
            setModalVisible={setIsModalVisible}
            onConfirm={() => goBack()}
        />
    </Container>;
}
